import { FileText, ArrowRight } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/shared/PageHero";
import { FAQ } from "@/components/shared/FAQ";
import { CTASection } from "@/components/shared/CTASection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const articles = [
  {
    title: "O que é Geração Distribuída (GD) e como funciona",
    description: "Entenda o modelo de geração distribuída de energia solar no Brasil, como funcionam os créditos e quem pode participar.",
    category: "Guia básico",
    readTime: "8 min",
    color: "from-blue-500 to-cyan-500",
  },
  {
    title: "Como fazer o rateio de créditos de energia corretamente",
    description: "Passo a passo para calcular a distribuição de créditos entre múltiplas unidades consumidoras sem erros.",
    category: "Tutorial",
    readTime: "12 min",
    color: "from-violet-500 to-purple-500",
  },
  {
    title: "Guia completo de cobrança para geradores de energia",
    description: "Melhores práticas para emitir boletos, configurar régua de cobrança e reduzir inadimplência.",
    category: "Guia avançado",
    readTime: "15 min",
    color: "from-emerald-500 to-teal-500",
  },
  {
    title: "Como escolher um software de gestão de GD",
    description: "Critérios importantes para avaliar plataformas de gestão de créditos de energia distribuída.",
    category: "Comparativo",
    readTime: "10 min",
    color: "from-orange-500 to-amber-500",
  },
  {
    title: "LGPD na gestão de energia: o que você precisa saber",
    description: "Obrigações legais para quem gerencia dados de consumidores de energia e como estar em conformidade.",
    category: "Compliance",
    readTime: "8 min",
    color: "from-pink-500 to-rose-500",
  },
  {
    title: "Automatização vs. planilhas: quando vale a pena migrar",
    description: "Análise de custos e benefícios de automatizar a gestão de créditos de energia.",
    category: "Análise",
    readTime: "6 min",
    color: "from-slate-500 to-slate-700",
  },
];

const faqItems = [
  {
    question: "O que são créditos de energia?",
    answer: "Créditos de energia são unidades de compensação geradas quando uma usina solar (ou outra fonte renovável) produz mais energia do que consome. Esses créditos podem ser transferidos para outras unidades consumidoras do mesmo titular ou grupo, reduzindo a conta de luz dessas unidades.",
  },
  {
    question: "Como funciona o rateio de créditos?",
    answer: "O rateio é a divisão dos créditos gerados entre múltiplas unidades consumidoras. Pode ser feito por porcentagem fixa (ex: UC1 recebe 30%, UC2 recebe 70%), por ordem de prioridade, ou por valor em kWh. A Cogni automatiza esse cálculo seguindo as regras que você configurar.",
  },
  {
    question: "Preciso de uma usina própria para usar a Cogni?",
    answer: "Não necessariamente. A Cogni atende tanto geradores donos de usinas quanto gestores de consórcios, cooperativas ou marketplaces que administram créditos de terceiros.",
  },
  {
    question: "A Cogni funciona com qualquer distribuidora?",
    answer: "Sim, temos integração com as principais distribuidoras do Brasil (CEMIG, CPFL, Enel, Energisa, Light, Neoenergia, Equatorial, EDP e outras). Para distribuidoras não listadas, oferecemos importação manual de faturas.",
  },
  {
    question: "Quanto tempo leva a implementação?",
    answer: "A implementação básica leva de 2 a 4 semanas, incluindo configuração do sistema, importação de dados históricos e treinamento da equipe. Projetos maiores ou com integrações customizadas podem levar até 6 semanas.",
  },
  {
    question: "Como é o modelo de cobrança da Cogni?",
    answer: "Trabalhamos com assinatura mensal baseada no volume de unidades consumidoras gerenciadas. Entre em contato para receber uma proposta personalizada de acordo com o tamanho da sua operação.",
  },
  {
    question: "Posso testar antes de contratar?",
    answer: "Sim, oferecemos uma demonstração guiada da plataforma com nosso time. Você pode ver a Cogni funcionando com dados de exemplo ou até mesmo com seus próprios dados de forma segura.",
  },
  {
    question: "Os dados ficam seguros?",
    answer: "Sim, utilizamos criptografia em trânsito e em repouso, arquitetura multi-tenant com isolamento de dados, backups diários e controles de acesso granulares. Veja mais detalhes na nossa página de Segurança.",
  },
];

export default function Recursos() {
  return (
    <Layout>
      <PageHero
        title="Recursos e Conteúdos"
        subtitle="Guias, tutoriais e informações para ajudar você a entender e otimizar sua gestão de créditos de energia."
      />

      {/* Articles Grid */}
      <section className="section-padding section-alt">
        <div className="container-wide">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-headline font-bold mb-4">
              Guias e artigos
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Conteúdos práticos para quem trabalha com energia distribuída.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {articles.map((article, index) => (
              <Card key={index} className="border-border hover:border-primary/30 transition-all cursor-pointer group shadow-lg hover:shadow-xl overflow-hidden">
                <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${article.color}`} />
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] sm:text-xs font-medium px-2 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-r ${article.color} text-white rounded-full`}>
                      {article.category}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground">{article.readTime}</span>
                  </div>
                  <CardTitle className="text-base sm:text-lg group-hover:text-primary transition-colors">
                    {article.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
                  <CardDescription className="text-xs sm:text-sm mb-3 sm:mb-4">
                    {article.description}
                  </CardDescription>
                  <span className="inline-flex items-center text-xs sm:text-sm font-medium text-primary">
                    Ler artigo
                    <ArrowRight className="ml-1 h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-6 sm:mt-8">
            <p className="text-muted-foreground text-sm">
              Novos conteúdos são publicados regularmente. Volte em breve para mais artigos.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        items={faqItems}
        title="Perguntas Frequentes"
        subtitle="Respostas para as dúvidas mais comuns sobre gestão de créditos de energia"
      />

      <CTASection
        title="Não encontrou o que procurava?"
        subtitle="Nossa equipe está pronta para responder suas dúvidas específicas."
        primaryCTA="Falar com especialista"
      />
    </Layout>
  );
}
